import React, {
    useEffect, useState, useRef, useLayoutEffect,
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import NavArrows from './components/NavArrows';
import { updateNavigation, getGtmAttributeProp, useCarouselLayoutUpdate } from './utils';

import './styles.scss';
import cssVars from './variables.scss';

import {
    baseClass, baseClassWithNavModifierClass,
    scrollWrapClass, scrollerClass, scrollerHasNavModifier,
    baseMaskClass,
    itemWrapClass, itemWrapSelectedClass, itemClass, itemSelectedMarkClass,
    itemWrapPrerenderedClass, itemWrapPrerenderedSelectedClass,
} from './classNames';

/**
 * @var {Number} scrollDistanceRatio how far will we scroll on nav arrow click? As a proportion of the total
 * scrollable distance
 */
const scrollDistanceRatio = 0.8;

/**
 * @var {Number} resizeObserverStartDelayMs need to allow time before resizeObserver - otherwise it fires
 * too early and we get wrong scroller dimensions.
 */
const resizeObserverStartDelayMs = 200;

/**
 * @var {Number} scrollToResizeObserverStartDelayMs if we have a selected item to scrollTo we need to allow
 * the scroller to finish scrolling before we start the observer or the scrolled distance has not settled and
 * we can't calculate nav arrow visibility correctly
 */
const scrollToResizeObserverStartDelayMs = 1000;

/**
 * Carousel component
 *
 * Pass in either:
 *  - items[] of { slug, display } for basic usage - pass in an onClick handler
 *  - an array of prerendered items
 * Navigation arrows display:
 *  - (default) inline to right of menu items
 *  - (if Header passed in) in header, to the right
 * Note that if you supply a pre-rendered header or menu items you may need to adjust the positioning of the
 * navigation arrows. See '& > &__nav' and '&__scroll-wrap > &__nav' CSS selectors in styles.scss
 * Pass in a scrollContainerRef if you need to access it (e.g. for scrollBy() functionality)
 * Pass in selectedItemSlugOrId === false to prevent the first item from being automatically selected
 * If you want the first element to be selected but not trigger the click event then you can set shouldTriggerFirstClick to false.
 *
 * @param {Object} props see proptypes
 * @returns {JSX} Carousel component
 */
function CarouselV2({
    items = [],
    jsxItems = [],
    jsxHeader = '',
    selectedItemSlugOrId = '',
    onItemClick = false,
    extraClass = '',
    scrollContainerRef = null,
    gtmComponents = [],
    shouldTriggerFirstClick = true,
    dataTestId = '',
}) {
    if (!items.length && !jsxItems.length) {
        return null;
    }

    const [selectedItem, setSelectedItem] = useState(selectedItemSlugOrId);
    const shouldUpdateLayout = useCarouselLayoutUpdate([items.length, jsxItems.length]);

    /**
     * @var {Object} navVisibilityRef used to track the visibility state of navigation elements, i.e. should
     * they be hidden, displayed, disabled, etc.
     */
    const navVisibilityRef = useRef(
        {
            left: null,
            right: null,
            global: null,
        },
    );
    const carouselRef = useRef(null);
    if (scrollContainerRef === null) {
        // eslint-disable-next-line no-param-reassign
        scrollContainerRef = useRef(null);
    }
    const navArrowsWidthRef = useRef(null);
    // if a header prop is passed the navArrows will appear inline in the scroll content with the navItems
    const navArrowsInline = !jsxHeader;

    const resizeHandler = (entries) => {
        // entries[0].target: { ... }
        const [{
            target: {
                scrollLeft: leftOffset,
                scrollWidth: contentWidth,
                clientWidth: containerWidth,
            },
        }] = entries;

        updateNavigation({
                leftOffset,
                contentWidth,
                containerWidth,
            },
            carouselRef,
            navVisibilityRef,
        );
    };

    const resizeObserver = useRef(new ResizeObserver(resizeHandler));

    useEffect(() => {
        return () => {
            if (resizeObserver.current) {
                resizeObserver.current.disconnect();
            }
        };
    }, []);

    useEffect(() => {
        if (!selectedItemSlugOrId) {
            // select first element
            const [item] = items;
            const firstItem = item?.slug || 0; // check with items and jsxItems
            setSelectedItem(firstItem);
            if (shouldTriggerFirstClick && onItemClick) {
                // second param to maintain consistency with clickHandler() call to onItemClick()
                onItemClick(firstItem, {});
            }
        }
    }, [items, selectedItemSlugOrId]);

    useEffect(() => {
        setSelectedItem(selectedItemSlugOrId);
    }, [selectedItemSlugOrId]);

    const clickHandler = (e) => {
        const clickedItem = e.target.dataset?.itemSlug || e.target.closest('[data-item-id]')?.dataset?.itemId;
        setSelectedItem(clickedItem);

        if (onItemClick) {
            onItemClick(e.target.closest('[data-item-extra-info]')?.dataset?.itemExtraInfo || clickedItem, e);
        }
    };

    // Note - would be nice to use IntersectionObserver to determine scroll boundaries, but...
    // https://github.com/w3c/IntersectionObserver/issues/301
    const scrollContent = (e) => {
        const { target: { dataset: { arrowDirection = null } } } = e;
        // allow user to click the mask to scroll as well - it might be covering the arrow
        const { target: { dataset: { mask: maskDirection = null } } } = e;
        const navDirection = arrowDirection || maskDirection;

        if (
            // allow mobile users to click the mask
            !maskDirection
            &&
            // desktop user checks - these elements not present on mobile
            (!navVisibilityRef.current.global || !navVisibilityRef.current[navDirection])
        ) {
            return;
        }

        const {
            current: {
                scrollLeft: leftOffset,
                scrollWidth: contentWidth,
                clientWidth: containerWidth,
            },
        } = scrollContainerRef;

        let scrollIncrement = parseInt(containerWidth * scrollDistanceRatio, 10);
        if (navDirection === 'left') {
            scrollIncrement *= -1;
        }
        const scrollOptions = { behavior: 'smooth', left: scrollIncrement };
        scrollContainerRef.current.scrollBy(scrollOptions);

        updateNavigation({
                leftOffset: leftOffset + scrollIncrement,
                contentWidth,
                containerWidth,
            },
            carouselRef,
            navVisibilityRef,
        );
    };

    useLayoutEffect(() => {
        const scrollContainerEl = scrollContainerRef.current;
        if (!scrollContainerEl) {

            return;
        }

        let scrollLeftPxToSelectedItem = 0; // default
        if (selectedItemSlugOrId !== false) {
            const {
                'nav-arrows-width-px': navWidthString,
                'nav-arrows-padding-right-px': navPaddingRightString,
            } = cssVars;
            if (navArrowsWidthRef.current === null) {
                const navWidth = parseInt(navWidthString, 10);
                if (!isNaN(navWidth)) {
                    navArrowsWidthRef.current = navWidth;
                } else {
                    navArrowsWidthRef.current = 0;
                }
            }
            let navRightPad = parseInt(navPaddingRightString, 10);
            if (isNaN(navRightPad)) {
                navRightPad = 0;
            }
            // Note - could integrate this in the IntersectionObserver implementation... whenever:
            // https://github.com/w3c/IntersectionObserver/issues/301 is fixed
            let navArrowsWidth = navArrowsWidthRef.current;
            if (!navArrowsInline) {
                // nav arrows are not in the scroller
                navArrowsWidth = 0;
            }
            const selectedItem = scrollContainerEl.querySelector(`.${itemWrapSelectedClass}`)
                || scrollContainerEl.querySelector(`.${itemWrapPrerenderedSelectedClass}`);
            const scrollerContentWidth = scrollContainerEl.scrollWidth;
            const scrollerVisibleWidth = scrollContainerEl.clientWidth;

            // do we need to scroll the item?
            if (selectedItem && (scrollerContentWidth > scrollerVisibleWidth)) {
                const scrollerLeftScrollOffset = scrollContainerEl.scrollLeft;
                const selectedItemWidth = selectedItem.scrollWidth;
                // how far from left scroller is the right most visible content - the RHS of visible content
                const scrollerContentRightOffset = scrollerLeftScrollOffset + scrollerVisibleWidth;
                // to LHS of total scroll container
                const selectedItemOffsetLeft = selectedItem.offsetLeft;
                // To RHS of same
                const selectedItemOffsetRight = selectedItemOffsetLeft + selectedItemWidth;
                // Determine the direction of the scroll
                const shouldScrollRight = selectedItemOffsetLeft < scrollerLeftScrollOffset;
                const shouldScrollLeft = selectedItemOffsetRight > (scrollerContentRightOffset - navArrowsWidth);
                // this variables is needed to stop carousel arrows update when the last visible items is selected
                const positionChanged = shouldScrollRight || shouldScrollLeft || shouldUpdateLayout;

                if (shouldScrollRight) {
                    // need to scroll right (negative left)
                    scrollLeftPxToSelectedItem = selectedItemOffsetLeft - scrollerLeftScrollOffset;
                } else if (shouldScrollLeft) {
                    // need to scroll left
                    scrollLeftPxToSelectedItem = selectedItemOffsetRight - scrollerContentRightOffset + navArrowsWidth + navRightPad;
                }

                if(positionChanged) {
                    updateNavigation({
                        leftOffset: scrollerLeftScrollOffset + scrollLeftPxToSelectedItem,
                        contentWidth: scrollerContentWidth + navRightPad,
                        containerWidth: scrollerVisibleWidth,
                    },
                    carouselRef,
                    navVisibilityRef,
                    );
                }
            }
        }

        if (scrollLeftPxToSelectedItem) {
            scrollContainerEl.scrollBy({ behavior: 'smooth', left: scrollLeftPxToSelectedItem });
        }
        setTimeout(
            () => { resizeObserver.current.observe(scrollContainerEl); },
            (scrollLeftPxToSelectedItem ? scrollToResizeObserverStartDelayMs : resizeObserverStartDelayMs)
        );
    }, [selectedItem, shouldUpdateLayout]);

    let renderedItems = [];
    if (items.length) {
        renderedItems = items.map(({ slug, display }) => {
            const isSelected = slug === selectedItem;
            const dataTestAttribute = dataTestId ? `${dataTestId}tab-${slug}` : null;

            return (
                <div key={slug} className={classNames(itemWrapClass, { [itemWrapSelectedClass]: isSelected })} >
                    <button
                        type="button"
                        data-item-slug={slug}
                        className={itemClass}
                        onClick={clickHandler}
                        { ...getGtmAttributeProp(gtmComponents, slug) }
                        data-testid={dataTestAttribute}
                    >
                        {display}
                    </button>
                    <div className={itemSelectedMarkClass} />
                </div>
            );
        });
    } else /* jsxItems.length */ {
        renderedItems = jsxItems.map((item, index) => {
            const isSelected = index === parseInt(selectedItem, 10);
            // Note that you will need to implement --selected styles yourself
            // @todo no use case for adding a dynamic actionable component to gtmAttributeProp yet. Pass in a function as a prop?

            return (
                <div
                    key={index}
                    data-item-id={index}
                    className={classNames(itemWrapPrerenderedClass, {[itemWrapPrerenderedSelectedClass]: isSelected})}
                    onClick={clickHandler}
                    { ...getGtmAttributeProp(gtmComponents) }
                >
                    {item}
                </div>
            );
        });
    }

    let renderedHeader = '';

    if (!navArrowsInline) {
        renderedHeader = <>
            {jsxHeader}
            <NavArrows
                clickHandler={scrollContent}
                initialNavVisibility={navVisibilityRef.current}
            />
        </>;
    }

    // Left and right masks start hidden. They will be set in the useLayoutEffect()
    // hook in the ResizeObserver callback
    return (
        <div data-testid="carousel" ref={carouselRef} className={classNames(baseClass, extraClass, { [baseClassWithNavModifierClass]: navVisibilityRef.current.global })}>
            {renderedHeader}
            <div className={scrollWrapClass}>
                <span data-mask="left" onClick={scrollContent} className={`${baseMaskClass}-left hidden`} />
                <span data-mask="right" onClick={scrollContent} className={`${baseMaskClass}-right hidden`} />
                {navArrowsInline
                    ? <NavArrows
                        clickHandler={scrollContent}
                        initialNavVisibility={navVisibilityRef.current}
                    />
                    : null
                }
                <div className={classNames(scrollerClass, { [scrollerHasNavModifier]: !navArrowsInline })} ref={scrollContainerRef}>
                    {renderedItems}
                </div>
            </div>
        </div>
    );
}

CarouselV2.propTypes = {
    items: PropTypes.arrayOf(
        PropTypes.shape({
            slug: PropTypes.string,
            display: PropTypes.string,
        })
    ),
    jsxItems: PropTypes.arrayOf(PropTypes.node),
    jsxHeader: PropTypes.node,
    selectedItemSlugOrId: PropTypes.oneOfType([
        PropTypes.string, PropTypes.number, PropTypes.bool,
    ]),
    onItemClick: PropTypes.func,
    extraClass: PropTypes.string,
    scrollContainerRef: PropTypes.object,
    gtmComponents: PropTypes.arrayOf(PropTypes.string),
    shouldTriggerFirstClick: PropTypes.bool,
    dataTestId: PropTypes.string,
};

CarouselV2.defaultProps = {
    items: [],
    jsxItems: [],
    jsxHeader: '',
    selectedItemSlugOrId: '',
    onItemClick: Function.prototype,
    extraClass: '',
    scrollContainerRef: null,
    gtmComponents: [],
    shouldTriggerFirstClick: true,
    dataTestId: '',
};

export default CarouselV2;
